import { MoonIcon, SunIcon } from '@heroicons/react/24/outline'
import { useSettings } from '@/contexts/SettingsContext'

const THEME_OPTIONS = [
  { value: 'light' as const, label: 'Light', icon: SunIcon },
  { value: 'dark' as const, label: 'Dark', icon: MoonIcon }
]

interface ThemeToggleProps {
  showLabels?: boolean
  className?: string
}

export function ThemeToggle({ showLabels = false, className = '' }: ThemeToggleProps) {
  const { settings, updateSetting } = useSettings()

  const currentTheme = THEME_OPTIONS.find(option => option.value === settings.theme) || THEME_OPTIONS[0]
  const CurrentIcon = currentTheme.icon

  const handleThemeChange = async (theme: typeof settings.theme) => {
    try {
      await updateSetting('theme', theme)
    } catch (error) {
      console.error('Failed to update theme:', error)
    }
  }

  const handleThemeClick = (theme: typeof settings.theme) => {
    void handleThemeChange(theme)
  }

  if (showLabels) {
    return (
      <div className={`flex rounded-lg border border-gray-200 dark:border-gray-600 ${className}`}>
        {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
          <button type="button"
            key={value}
            onClick={() => { handleThemeClick(value) }}
            className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium transition-colors first:rounded-l-lg last:rounded-r-lg ${
              settings.theme === value
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="relative group">
      <button type="button"
        onClick={() => {
          const currentIndex = THEME_OPTIONS.findIndex(option => option.value === settings.theme)
          const nextIndex = (currentIndex + 1) % THEME_OPTIONS.length
          handleThemeClick(THEME_OPTIONS[nextIndex].value)
        }}
        className={`p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors ${className}`}
        title={`Current theme: ${currentTheme.label}. Click to cycle themes.`}
      >
        <CurrentIcon className="w-5 h-5" />
      </button>
      
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        {currentTheme.label} theme
      </div>
    </div>
  )
}
